import apiClient from "../../apis/api-client";
import FileSaver from "file-saver";
import { CloudGovApiError } from "../api-error";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";

export type OperationState = "DOWNLOAD" | "PREVIEW" | "RESULT";

export type UpdateProcedureGroupPreviewResponse = {
  results: {
    csvRow: {
      procedureId: string;
      procedureName: string;
      groupId: string;
    };
    validateError?: {
      severity: "ERROR";
      errCode: string;
    };
  }[];
};

export type UpdateProcedureGroupResponse = {
  count: number;
};

const errorCodes = ["group_id.not_exists"] as const;
type errorCodesLiteral = (typeof errorCodes)[number];

export const convertErrorCode = (code: string) => {
  const errMessageLiteral = {
    "group_id.not_exists": "グループIDが存在しません",
  };

  return errMessageLiteral[code as errorCodesLiteral];
};

enum ErrorMessages {
  TOO_LARGE_RECORD_COUNT = "処理に失敗しました。CSVの行数が出力上限を超えました。",
  INVALID_CSV_FORMAT = "処理に失敗しました。CSVの構造が正しくありません。",
  INVALID_CSV_CONTENT = "処理に失敗しました。CSVの内容が正しくありません。",
  UNEXPECTED_ERROR = "処理に失敗しました。CSV出力でエラーが発生しました",
  NO_PERMISSION = "処理に失敗しました。CSV出力を実行する権限がありません。",
}

export const downloadCSVForUpdateGroup = async (
  orgId: string,
  groupIds: string[],
): Promise<void | string> => {
  try {
    const { data } = await apiClient.downloadProcedureCSVForUpdateGroup(
      orgId,
      groupIds,
    );
    FileSaver.saveAs(new Blob([data]), "procedures.csv");
  } catch (e) {
    const apiError = new CloudGovApiError(e);
    if (CloudGovApiError.is403(apiError)) {
      throw ErrorMessages.NO_PERMISSION;
    }

    await apiError.resolveErrorResponse();

    switch (apiError.errorCode()) {
      case "bad_request.too_large_record_count":
        throw ErrorMessages.TOO_LARGE_RECORD_COUNT;
    }

    reportUnexpectedError(e);
    throw ErrorMessages.UNEXPECTED_ERROR;
  }
};

export const bulkUpdateGroupPreview = async (
  csv: string,
  orgId: string,
): Promise<UpdateProcedureGroupPreviewResponse | string> => {
  try {
    const { data } = await apiClient.bulkUpdateProcedureGroupPreview({
      data: csv,
      orgId,
    });

    return data as UpdateProcedureGroupPreviewResponse;
  } catch (e) {
    throw await handleBulkUpdateGroupError(e);
  }
};

export const bulkUpdateGroup = async (
  csv: string,
  orgId: string,
): Promise<UpdateProcedureGroupResponse | string> => {
  try {
    const { data } = await apiClient.bulkUpdateProcedureGroup({
      data: csv,
      orgId: orgId,
    });

    return data as UpdateProcedureGroupResponse;
  } catch (e) {
    throw handleBulkUpdateGroupError(e);
  }
};

const handleBulkUpdateGroupError = (e: Error): string => {
  const apiError = new CloudGovApiError(e);
  if (apiError.errorCode() === "bad_request.invalid_csv_format") {
    return ErrorMessages.INVALID_CSV_FORMAT;
  }
  if (apiError.errorCode() === "bad_request.too_large_record_count") {
    return ErrorMessages.TOO_LARGE_RECORD_COUNT;
  }
  if (apiError.errorCode() === "bad_request") {
    return ErrorMessages.INVALID_CSV_CONTENT;
  }

  reportUnexpectedError(e);
  return ErrorMessages.UNEXPECTED_ERROR;
};
